import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Email,
  Phone,
  Person,
  AccessTime,
  Delete,
  Search,
  Refresh,
} from '@mui/icons-material';
import { getAllMessages, deleteMessage, adminLogout } from '../../utils/api';
import AdminLayout from '../../components/AdminLayout';

const MessageCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
}));

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await getAllMessages();

      if (response.data && response.data.success) {
        setMessages(response.data.messages || []);
      } else {
        showSnackbar('Failed to load messages: ' + (response.data?.message || 'Unknown error'), 'error');
      }
    } catch (error) {

      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        showSnackbar('Failed to load messages: ' + errorMessage, 'error');
      } else if (error.request) {
        // Network error
        showSnackbar('Failed to load messages: Network error', 'error');
      } else {
        // Other error
        showSnackbar('Failed to load messages: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = async (messageId, messageName) => {
    if (window.confirm(`Are you sure you want to delete the message from "${messageName}"?`)) {
      try {
        await deleteMessage(messageId);
        showSnackbar('Message deleted successfully');
        fetchMessages();
        if (selectedMessage && selectedMessage.id === messageId) {
          handleCloseDialog();
        }
      } catch (error) {
        showSnackbar('Failed to delete message', 'error');
      }
    }
  };

  const filteredMessages = messages.filter(message =>
    message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const messageDate = new Date(dateString);
    const diffTime = Math.abs(now - messageDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return messageDate.toLocaleDateString();
  };

  return (
    <AdminLayout title="View Messages">
      <Container maxWidth="lg">
        {/* Header Actions */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{
                fontWeight: 600,
                fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2rem' }
              }}>
                Messages Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Messages: {messages.length} | Unread: {messages.filter(m => !m.read).length}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchMessages}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Messages List */}
            {filteredMessages.length === 0 ? (
              <Card sx={{ p: 6, textAlign: 'center' }}>
                <Email sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchTerm ? 'No messages found matching your search' : 'No messages yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'Customer inquiries will appear here when they contact you through the website.'}
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {filteredMessages.map((message) => (
                  <Grid item xs={12} key={message.id}>
                    <MessageCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Person sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {message.name}
                              </Typography>
                              {/* <Chip 
                                label={getTimeAgo(message.created_at)} 
                                size="small" 
                                sx={{ ml: 2 }} 
                              /> */}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Email sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                              <Typography variant="body2" color="text.secondary">
                                {message.email}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Phone sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                              <Typography variant="body2" color="text.secondary">
                                {message.mobile}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTime sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(message.created_at)}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            borderLeft: '4px solid',
                            borderLeftColor: 'primary.main'
                          }}
                        >
                          {message.message}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewMessage(message)}
                          >
                            View Details
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteMessage(message.id, message.name)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </MessageCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Message Detail Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Message Details
          </DialogTitle>
          <DialogContent>
            {selectedMessage && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedMessage.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedMessage.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Mobile
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedMessage.mobile}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Received
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDate(selectedMessage.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Message
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Typography variant="body1">
                        {selectedMessage.message}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={() => {
                handleDeleteMessage(selectedMessage.id, selectedMessage.name);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminLayout>
  );
};

export default Messages;